import { Link } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import type { ETFRow } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type { ETFSortColumnId } from '#/components/index/ETFsExploreTable/types';
import { Indicator } from '#/components/ui/Indicator';
import { NoData } from '#/components/ui/NoData';
import { Ticker } from '#/components/ui/Ticker';
import { DASH } from '#/utils/constants';
import { formatCurrency, roundDecimal } from '#/utils/format';

export function etfRowId(row: ETFRow, index: number) {
  return `etf-${row.ticker ?? row.name}-${index}`;
}

export function getSortColumn(
  sorting: SortingState,
  fallbackSortColumn: ETFSortColumnId,
): ETFSortColumnId {
  const columnId = sorting[0]?.id;

  if (
    columnId === 'expense_ratio' ||
    columnId === 'price' ||
    columnId === 'trailing_1yr_return' ||
    columnId === 'return_5Y_annualized'
  ) {
    return columnId;
  }

  return fallbackSortColumn;
}

export function getSortDirection(sorting: SortingState): 'asc' | 'desc' {
  return sorting[0]?.desc === false ? 'asc' : 'desc';
}

const API_SORT_KEY_BY_COLUMN: Record<
  ETFSortColumnId,
  'expense_ratio' | 'price' | 'return_1y' | 'return_5y'
> = {
  expense_ratio: 'expense_ratio',
  price: 'price',
  trailing_1yr_return: 'return_1y',
  return_5Y_annualized: 'return_5y',
};

export function getApiSortKey(sortColumn: ETFSortColumnId) {
  return API_SORT_KEY_BY_COLUMN[sortColumn];
}

interface ETFColumnOptions {
  /**
   * Overrides the ticker link's analytics annotation. Defaults to the explore
   * table's `explore_table_ticker` / `etfs_explore`; the search page passes
   * `search_result_row` / `search_results` so its rows are attributed
   * distinctly without affecting other tables.
   */
  tickerTrack?: { track: string; context: string };
}

export function etfsColumns(
  firstColumnLabel: string,
  options?: ETFColumnOptions,
): ColumnDef<ETFRow>[] {
  const tickerTrack = options?.tickerTrack ?? {
    track: 'explore_table_ticker',
    context: 'etfs_explore',
  };

  return [
    {
      id: 'ticker',
      accessorKey: 'ticker',
      header: firstColumnLabel,
      enableSorting: false,
      size: 300,
      minSize: 220,
      meta: { announceHeader: true },
      cell: ({ row }) => {
        if (!row.original.ticker && !row.original.name) {
          return <NoData />;
        }

        const title = row.original.name ?? DASH;

        return (
          <span className="flex min-w-0 items-center gap-8">
            <Ticker symbol={row.original.ticker ?? DASH} size="md" />
            {row.original.ticker ? (
              <Link
                to="/$category/$id"
                params={{ category: 'etfs', id: row.original.ticker }}
                className="text-static-12 min-w-0 truncate"
                data-track={tickerTrack.track}
                data-track-label={row.original.ticker}
                data-track-context={tickerTrack.context}
              >
                {title}
              </Link>
            ) : (
              <span className="text-static-12 min-w-0 truncate">{title}</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'expense_ratio',
      header: 'Expense Ratio',
      accessorKey: 'expense_ratio',
      enableSorting: true,
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        return value === null ? <NoData /> : `${roundDecimal(value) ?? 0}%`;
      },
    },
    {
      id: 'price',
      header: 'Price',
      accessorKey: 'price',
      enableSorting: true,
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        return formatCurrency(getValue<number | null>()) ?? <NoData />;
      },
    },
    {
      id: 'return_5Y_annualized',
      accessorKey: 'return_5Y_annualized',
      header: '5-Year Return',
      enableSorting: true,
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className="text-static-14"
            size="md"
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
    {
      id: 'trailing_1yr_return',
      accessorKey: 'trailing_1yr_return',
      header: '1-Year Return',
      enableSorting: true,
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className="text-static-14"
            size="md"
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
  ];
}
